/*
 * Meraas Gallery — site chrome behaviour (announcement, localization dropdowns, header).
 * New file: Hyper's theme.js / header.js are never edited (see base.scss rule 1).
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------
   * <mg-rotator data-interval="4200"> — shows its children one at a time
   * (design/index.html:2124 · 4.2s, fade + translateY(8px) in CSS)
   * ------------------------------------------------------------------ */
  class MgRotator extends HTMLElement {
    connectedCallback() {
      this.items = [...this.children];
      this.index = Math.max(0, this.items.findIndex((el) => el.classList.contains('is-on')));
      this.start();
      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          this.stop();
          this.show(this.items.indexOf(event.target));
        });
        this.addEventListener('shopify:block:deselect', () => this.start());
      }
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      this.stop();
      if (this.items.length < 2 || reduceMotion.matches) return;
      const interval = Math.max(1000, parseInt(this.dataset.interval, 10) || 4200);
      this.timer = setInterval(() => {
        if (!document.hidden) this.show((this.index + 1) % this.items.length);
      }, interval);
    }

    stop() {
      clearInterval(this.timer);
    }

    show(index) {
      if (index < 0) return;
      this.items[this.index]?.classList.remove('is-on');
      this.index = index;
      this.items[this.index]?.classList.add('is-on');
    }
  }
  if (!customElements.get('mg-rotator')) customElements.define('mg-rotator', MgRotator);

  /* ------------------------------------------------------------------
   * <mg-disclosure> — button [data-disclosure-btn] toggles .is-open
   * closes on outside click, focus leaving, Escape (design .lang-dd)
   * ------------------------------------------------------------------ */
  class MgDisclosure extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-disclosure-btn]');
      if (!this.button) return;
      this.onDocClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.onFocusIn = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.button.addEventListener('click', () => (this.classList.contains('is-open') ? this.close() : this.open()));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) {
          event.stopPropagation();
          this.close();
          this.button.focus();
        }
      });
    }

    open() {
      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('focusin', this.onFocusIn);
    }

    close() {
      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('focusin', this.onFocusIn);
    }
  }
  if (!customElements.get('mg-disclosure')) customElements.define('mg-disclosure', MgDisclosure);
  /* ------------------------------------------------------------------
   * <mg-mega-tabs> — design mega: hovering / focusing / clicking a level-2
   * tab shows its panel (design/index.html:2006 setMegaTab)
   * ------------------------------------------------------------------ */
  class MgMegaTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-mega-tab]')];
      this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      const pick = (event) => {
        const tab = event.target.closest('[data-mega-tab]');
        if (tab && this.contains(tab)) this.select(this.tabs.indexOf(tab));
      };
      this.addEventListener('mouseover', pick);
      this.addEventListener('focusin', pick);
      this.addEventListener('click', pick);
    }

    select(index) {
      this.tabs.forEach((tab, i) => tab.setAttribute('aria-expanded', String(i === index)));
      this.panels.forEach((panel, i) => panel && (panel.hidden = i !== index));
    }
  }
  if (!customElements.get('mg-mega-tabs')) customElements.define('mg-mega-tabs', MgMegaTabs);

  /* ------------------------------------------------------------------
   * <mg-search-overlay id="MgSearch"> — the design's search panel that slides
   * from the top (design :279). Wraps Hyper's <predictive-search>, which keeps
   * fetching results; this element only opens / closes / traps the panel.
   * ------------------------------------------------------------------ */
  class MgSearchOverlay extends HTMLElement {
    connectedCallback() {
      this.controls = [...document.querySelectorAll(`[aria-controls="${this.id}"]`)];
      this.input = this.querySelector('input[type="search"]');
      this.controls.forEach((btn) => btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.isOpen ? this.close() : this.open(btn);
      }));
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-search-close]')) this.close();
      });
      this.onKey = (event) => {
        if (event.key === 'Escape') this.close();
      };
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      this.trigger = trigger;
      this.classList.add('is-open');
      document.body.classList.add('mg-search-open');
      this.controls.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      document.addEventListener('keydown', this.onKey);
      setTimeout(() => this.input && this.input.focus({ preventScroll: true }), 60);
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.body.classList.remove('mg-search-open', 'search-open');
      this.controls.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      document.removeEventListener('keydown', this.onKey);
      const predictive = this.querySelector('predictive-search');
      if (predictive && typeof predictive.toggleSearchState === 'function') predictive.toggleSearchState(false);
      this.input && this.input.blur();
      this.trigger && this.trigger.focus({ preventScroll: true });
    }
  }
  if (!customElements.get('mg-search-overlay')) customElements.define('mg-search-overlay', MgSearchOverlay);

  /* ------------------------------------------------------------------
   * Header: border + soft shadow once the page scrolls past 40px (design :2156)
   * ------------------------------------------------------------------ */
  const header = document.querySelector('.header--mg');
  if (header) {
    const onScroll = () => header.classList.toggle('is-stuck', window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
  /* ------------------------------------------------------------------
   * Footer (footer_style "meraas"): menu columns are open from 768px up and
   * fold into accordions below (design :2159) · newsletter validates inline
   * (Q13 — no modal) before the native customer form posts.
   * ------------------------------------------------------------------ */
  const fcols = [...document.querySelectorAll('.footer--mg [data-fcol]')];
  if (fcols.length) {
    const wide = window.matchMedia('(min-width: 768px)');
    const sync = () => fcols.forEach((d) => (d.open = wide.matches));
    sync();
    wide.addEventListener('change', sync);
    fcols.forEach((d) => d.querySelector('summary')?.addEventListener('click', (event) => {
      if (wide.matches) event.preventDefault();
    }));
  }
  document.querySelectorAll('form[data-mg-news]').forEach((form) => {
    const input = form.querySelector('input[type="email"]');
    const err = form.querySelector('[data-mg-news-err]');
    const ok = form.querySelector('[data-mg-news-ok]');
    if (ok) ok.focus({ preventScroll: true });
    if (!input || !err) return;
    const valid = (show) => {
      const isValid = input.value.trim() !== '' && input.checkValidity();
      if (show || form.classList.contains('is-invalid')) {
        form.classList.toggle('is-invalid', !isValid);
        err.hidden = isValid;
        input.setAttribute('aria-invalid', String(!isValid));
      }
      return isValid;
    };
    form.addEventListener('submit', (event) => {
      if (!valid(true)) {
        event.preventDefault();
        input.focus();
      }
    });
    input.addEventListener('input', () => valid(false));
  });
  /* ------------------------------------------------------------------
   * <mg-tiers> — volume-discount progress (snippets/mg-volume-tiers.liquid)
   * Liquid renders the first state; this re-renders on every cart change.
   * mg-chrome.js runs before Hyper's theme.js (both deferred), so the
   * subscription waits for DOMContentLoaded when FoxTheme.pubsub is not ready.
   * ------------------------------------------------------------------ */
  const whenTheme = (fn) => {
    if (window.FoxTheme && FoxTheme.pubsub) fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  };

  /* ------------------------------------------------------------------
   * --scrollbar-width (used by .modal-show { padding-right } while a drawer locks scroll).
   * Hyper measures it once (innerWidth - body.clientWidth) and never resets it to 0, so after a
   * resize to mobile — or with any horizontal overflow — opening the menu drawer pushed the page
   * in by a phantom gutter. Measure the real scrollbar thickness (0 for overlay scrollbars) after
   * Hyper's own pass and on resize; skip while a lock class has already hidden the scrollbar.
   * ------------------------------------------------------------------ */
  const scrollLockClasses = ['modal-show', 'modal-showing', 'body-no-scrollbar', 'search-open'];
  const measureScrollbar = () => {
    if (scrollLockClasses.some((name) => document.body.classList.contains(name))) return;
    const probe = document.createElement('div');
    // display:block — Hyper's div:empty { display: none } would otherwise measure 0 for every scrollbar
    probe.style.cssText = 'display:block;position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;';
    document.body.appendChild(probe);
    const thickness = probe.offsetWidth - probe.clientWidth;
    probe.remove();
    const hasScrollbar = document.documentElement.clientWidth < window.innerWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${hasScrollbar ? thickness : 0}px`);
  };
  let scrollbarFrame;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(scrollbarFrame);
    scrollbarFrame = requestAnimationFrame(measureScrollbar);
  });
  // Hyper's DOMready pass runs after this deferred file's listeners → measure on the next task
  whenTheme(() => setTimeout(measureScrollbar));
  window.addEventListener('load', measureScrollbar);

  class MgTiers extends HTMLElement {
    connectedCallback() {
      try {
        this.tiers = JSON.parse(this.dataset.tiers || '[]');
        this.i18n = JSON.parse(this.querySelector('[data-tiers-i18n]').textContent);
      } catch (e) {
        return;
      }
      this.near = parseInt(this.dataset.near, 10) || 3;
      this.plural = new Intl.PluralRules(document.documentElement.lang || 'en');
      whenTheme(() => {
        if (!window.FoxTheme || !FoxTheme.pubsub) return;
        this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.cart && typeof event.cart.item_count === 'number') this.render(event.cart.item_count);
        });
      });
    }

    disconnectedCallback() {
      this.unsubscribe && this.unsubscribe();
    }

    pct(d) {
      return `<bdi dir="ltr">${d}%</bdi>`;
    }

    items(n) {
      const [rep, text] = this.i18n.items[this.plural.select(n)] || this.i18n.items.other;
      return text.replace(String(rep), String(n));
    }

    fill(n) {
      let prev = 0;
      for (let i = 0; i < this.tiers.length; i++) {
        const q = this.tiers[i].q;
        if (n < q) return ((i + (n - prev) / (q - prev)) * 100) / this.tiers.length;
        prev = q;
      }
      return 100;
    }

    render(n) {
      const t = this.tiers;
      if (!t.length) return;
      const cur = t.filter((x) => n >= x.q).pop() || null;
      const next = t.find((x) => n < x.q) || null;
      const left = next ? next.q - n : 0;
      const fmt = (s, map) => Object.keys(map).reduce((out, k) => out.split(`[${k}]`).join(map[k]), s);
      let msg;
      if (!next) msg = fmt(this.i18n.max, { c: this.pct(cur.d) });
      else if (left <= this.near)
        msg = cur
          ? fmt(this.i18n.next, { c: this.pct(cur.d), x: this.items(left), d: this.pct(next.d) })
          : fmt(this.i18n.next0, { x: this.items(left), d: this.pct(next.d) });
      else if (cur) msg = fmt(this.i18n.now, { c: this.pct(cur.d) });
      else msg = fmt(this.i18n.start, { x: t[0].q, d: this.pct(t[0].d) });

      this.classList.toggle('is-on', !!cur);
      this.dataset.count = n;
      const bar = this.querySelector('.tiers__bar');
      if (bar) {
        bar.setAttribute('aria-valuenow', String(Math.min(n, t[t.length - 1].q)));
        const fill = bar.querySelector('i');
        if (fill) fill.style.width = `${Math.round(this.fill(n) * 100) / 100}%`;
      }
      this.querySelectorAll('.tiers__steps li').forEach((li, i) => li.classList.toggle('is-hit', !!t[i] && n >= t[i].q));
      const out = this.querySelector('.tiers__msg');
      if (out) out.innerHTML = msg;
    }
  }
  if (!customElements.get('mg-tiers')) customElements.define('mg-tiers', MgTiers);
  /* ------------------------------------------------------------------
   * Cart drawer (drawer_style "meraas"): "Order note" opens the textarea inline
   * (design :2081). Delegated — cart.js replaces the drawer footer on every change.
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-mg-note-toggle]');
    if (!toggle) return;
    const field = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!field) return;
    field.hidden = !field.hidden;
    toggle.setAttribute('aria-expanded', String(!field.hidden));
    if (!field.hidden) field.focus();
  });
  /* ------------------------------------------------------------------
   * <mg-qv-media id="MediaGallery-{section}"> — quick view media (quick_view_type "meraas").
   * product-info calls init() + setActiveMedia(variant) on this id when the variant changes.
   * ------------------------------------------------------------------ */
  class MgQvMedia extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-qv-main]');
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-qv-thumb]');
        if (thumb) this.activate(thumb);
      });
    }

    init() {}

    activate(thumb) {
      if (!thumb || !this.main) return;
      this.main.removeAttribute('srcset');
      this.main.src = thumb.dataset.src;
      this.querySelectorAll('[data-qv-thumb]').forEach((b) => b.setAttribute('aria-current', String(b === thumb)));
    }

    setActiveMedia(variant) {
      const id = variant && variant.featured_media && variant.featured_media.id;
      if (id) this.activate(this.querySelector(`[data-qv-thumb][data-media-id="${id}"]`));
    }
  }
  if (!customElements.get('mg-qv-media')) customElements.define('mg-qv-media', MgQvMedia);
  /* ------------------------------------------------------------------
   * Toast (compare limit only). Add to cart just opens the cart drawer — Hyper's ProductForm
   * does that itself and puts .btn--loading on the button while the request runs.
   * ------------------------------------------------------------------ */
  const toast = document.getElementById('MgToast');
  let toastTimer;
  const showToast = (message) => {
    if (!toast) return;
    const msg = toast.querySelector('[data-toast-msg]');
    if (msg) msg.textContent = message;
    toast.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-on'), 2600);
  };
  const templateText = (sel) => (toast && toast.querySelector(sel) ? toast.querySelector(sel).innerHTML.trim() : '');

  // cart count "bump" whenever the cart changes (design .cart-count.bump)
  whenTheme(() => {
    if (!window.FoxTheme || !FoxTheme.pubsub) return;
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
      document.querySelectorAll('.header__cart-count').forEach((el) => {
        el.classList.remove('is-bump');
        void el.offsetWidth;
        el.classList.add('is-bump');
      });
    });
  });

  // Compare: over the limit → toast instead of Hyper's alert() (design cmpMax). Capture phase runs
  // before compare-checkbox's own change handler on the input.
  document.addEventListener('change', (event) => {
    const box = event.target;
    if (!box.matches || !box.matches('.js-compare-checkbox') || !box.checked) return;
    const max = (window.FoxTheme && FoxTheme.compare && FoxTheme.compare.maxProductsInCompare) || 4;
    const list = (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.getStorage('hypertheme:compare-products')) || [];
    if (list.length >= max && !list.some((p) => String(p.id) === box.dataset.productId)) {
      event.stopImmediatePropagation();
      box.checked = false;
      showToast(templateText('[data-toast-compare-max]'));
    }
  }, true);
  /* ------------------------------------------------------------------
   * Compare tray (pcard_style "meraas"): visible while 1+ products are picked;
   * body.has-cmp lifts the WhatsApp button and the toast above it (design kit :866).
   * ------------------------------------------------------------------ */
  const cmpBar = document.querySelector('compare-bar.compare-bar--mg');
  if (cmpBar) {
    const syncCmp = (count) => {
      cmpBar.hidden = !(count > 0);
      document.body.classList.toggle('has-cmp', count > 0);
    };
    whenTheme(() => {
      const read = () => ((window.FoxTheme && FoxTheme.utils && FoxTheme.utils.getStorage('hypertheme:compare-products')) || []).length;
      syncCmp(read());
      if (window.FoxTheme && FoxTheme.pubsub) {
        FoxTheme.pubsub.subscribe('compare:add', (e) => syncCmp(e.products.length));
        FoxTheme.pubsub.subscribe('compare:remove', (e) => syncCmp(e.products.length));
      }
    });
  }
  /* ------------------------------------------------------------------
   * Menu drawer (one-row header): Hyper keeps <menu-drawer> inside the header,
   * so the header section is raised above the announcement bar while it is open.
   * ------------------------------------------------------------------ */
  const menuDrawer = document.querySelector('menu-drawer.menu-drawer--mg');
  if (menuDrawer) {
    menuDrawer.addEventListener('toggle', () => {
      document.body.classList.toggle('mg-menu-open', menuDrawer.hasAttribute('open'));
    });
  }
})();
